// ── Distance & area ────────────────────────────────────────────────────────────

/** Haversine distance between two [lng, lat] points, in metres. */
export function haversineMetres([lng1, lat1]: number[], [lng2, lat2]: number[]): number {
    const R = 6_371_000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  
  /**
   * Approximate planar area of a polygon ring (as [lng, lat][] vertices) in m².
   * Uses the shoelace formula scaled by a per-latitude degree-to-metre factor.
   * Sufficient precision for UI display; not suitable for geodetic computation.
   */
  export function polygonAreaM2(coords: number[][]): number {
    let area = 0;
    const n = coords.length;
    for (let i = 0; i < n; i++) {
      const [x1, y1] = coords[i];
      const [x2, y2] = coords[(i + 1) % n];
      area += (x2 - x1) * (y2 + y1);
    }
    const avgLat = coords.reduce((s, c) => s + c[1], 0) / n;
    const mPerDegLat = 111_320;
    const mPerDegLng = 111_320 * Math.cos((avgLat * Math.PI) / 180);
    return Math.abs(area / 2) * mPerDegLat * mPerDegLng;
  }
  
  // ── Geometry helpers ───────────────────────────────────────────────────────────
  
  /** Midpoint of two coordinate pairs. */
  export function midpoint([x1, y1]: number[], [x2, y2]: number[]): [number, number] {
    return [(x1 + x2) / 2, (y1 + y2) / 2];
  }
  
  /** Format a distance in metres as a short German-style string. */
  export function fmtDist(m: number): string {
    return m < 1_000 ? `${Math.round(m)} m` : `${(m / 1_000).toFixed(2)} km`;
  }
  
  /**
   * Approximate a circle as a closed polygon ring.
   * @param center   [lng, lat] of the centre
   * @param radiusM  radius in metres
   * @param numPoints number of ring vertices (min 6 for a hexagon)
   */
  export function generateCirclePoints(
    center: [number, number],
    radiusM: number,
    numPoints: number,
  ): [number, number][] {
    const R = 6_371_000;
    const [lng0, lat0] = center;
    const lat0Rad = (lat0 * Math.PI) / 180;
    const lng0Rad = (lng0 * Math.PI) / 180;
    const d = radiusM / R;
  
    const pts: [number, number][] = [];
    for (let i = 0; i < numPoints; i++) {
      const bearing = (2 * Math.PI * i) / numPoints;
      const lat = Math.asin(
        Math.sin(lat0Rad) * Math.cos(d) +
        Math.cos(lat0Rad) * Math.sin(d) * Math.cos(bearing),
      );
      const lng = lng0Rad + Math.atan2(
        Math.sin(bearing) * Math.sin(d) * Math.cos(lat0Rad),
        Math.cos(d) - Math.sin(lat0Rad) * Math.sin(lat),
      );
      pts.push([(lng * 180) / Math.PI, (lat * 180) / Math.PI]);
    }
    return pts;
  }
  
  // ── Coordinate encoding ────────────────────────────────────────────────────────
  
  /**
   * Decode the app's "lat lng lat lng …" encoded-content string into
   * Mapbox-compatible [lng, lat][] pairs.
   */
  export function decodeCoordinates(encoded: string): [number, number][] {
    const parts = encoded.trim().split(/\s+/).map(Number);
    const coords: [number, number][] = [];
    for (let i = 0; i + 1 < parts.length; i += 2) {
      const lat = parts[i];
      const lng = parts[i + 1];
      if (!isNaN(lat) && !isNaN(lng)) coords.push([lng, lat]);
    }
    return coords;
  }
  
  /**
   * Encode [lng, lat][] vertices back to the app's "lat lng lat lng …" format.
   * (Inverse of decodeCoordinates.)
   */
  export function encodeCoordinates(vertices: [number, number][]): string {
    return vertices.map(([lng, lat]) => `${lat} ${lng}`).join(' ');
  }
  
  /**
   * A representative [lng, lat] for a POI, whatever its geometry.
   *
   * Points carry `locationCoordinates`, but lines and polygons only have
   * `encodedContent` (multi-part polygons separated by ' | '), so they'd otherwise
   * resolve to no location at all. For those we use the mean of the vertices — the
   * same thing turf's `centroid` does. Good enough to hang a marker or a work order on;
   * it isn't the true area centre of mass, and a closed ring's repeated first vertex
   * biases it very slightly.
   */
  export function poiCenter(poi: any): [number, number] | null {
    if (!poi) return null;
  
    const lat = poi.locationCoordinates?.latitude ?? poi.location?.latitude;
    const lng = poi.locationCoordinates?.longitude ?? poi.location?.longitude;
    if (lat != null && lng != null) return [lng, lat];
  
    const encoded: string = poi.encodedContent ?? '';
    if (!encoded) return null;
  
    const coords = encoded
      .split(' | ')
      .flatMap(part => decodeCoordinates(part.trim()));
    if (!coords.length) return null;
  
    const [sumLng, sumLat] = coords.reduce(
      ([accLng, accLat], [lngV, latV]) => [accLng + lngV, accLat + latV],
      [0, 0],
    );
    return [sumLng / coords.length, sumLat / coords.length];
  }
  
  /** A representative [lng, lat] for a game record (kill site, else plain location). */
  export function gameRecordCenter(record: any): [number, number] | null {
    if (!record) return null;
    const lat = record.placeOfKilling?.location?.latitude
      ?? record.locationCoordinates?.latitude
      ?? record.location?.latitude;
    const lng = record.placeOfKilling?.location?.longitude
      ?? record.locationCoordinates?.longitude
      ?? record.location?.longitude;
    return (lat != null && lng != null) ? [lng, lat] : null;
  }
  
  // ── GeoJSON vertex extraction ──────────────────────────────────────────────────
  
  /**
   * Flatten all vertices from a GeoJSON feature collection into [lng, lat][] pairs.
   * Supports Point, LineString, and Polygon geometries.
   * Polygon rings: the duplicated closing vertex is omitted.
   */
  export function extractVerticesFromFeatures(features: GeoJSON.Feature[]): [number, number][] {
    const out: [number, number][] = [];
    for (const f of features) {
      const g = f.geometry;
      if (!g) continue;
      if (g.type === 'Point') {
        const c = (g as GeoJSON.Point).coordinates;
        if (c.length >= 2) out.push([c[0], c[1]]);
      } else if (g.type === 'LineString') {
        for (const c of (g as GeoJSON.LineString).coordinates) {
          if (c.length >= 2) out.push([c[0], c[1]]);
        }
      } else if (g.type === 'Polygon') {
        const ring = (g as GeoJSON.Polygon).coordinates[0] ?? [];
        for (let i = 0; i < ring.length; i++) {
          // Skip the ring's duplicated closing vertex
          if (
            i === ring.length - 1 &&
            ring[i][0] === ring[0][0] &&
            ring[i][1] === ring[0][1]
          ) continue;
          out.push([ring[i][0], ring[i][1]]);
        }
      }
    }
    return out;
  }
  
  // ── Colour helpers ─────────────────────────────────────────────────────────────
  
  /**
   * Convert a 6- or 8-digit hex colour string to a CSS rgba() value.
   * Passthrough for any value that doesn't match (returns as-is).
   * Examples: "#FF0000" → "rgba(255,0,0,1)", "#FF000080" → "rgba(255,0,0,0.5)"
   */
  export function hexToRgba(hex: string): string {
    if (!hex) return hex;
    const m8 = hex.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (m8) {
      const r = parseInt(m8[1], 16);
      const g = parseInt(m8[2], 16);
      const b = parseInt(m8[3], 16);
      const a = Math.round((parseInt(m8[4], 16) / 255) * 100) / 100;
      return `rgba(${r},${g},${b},${a})`;
    }
    const m6 = hex.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (m6) {
      return `rgba(${parseInt(m6[1], 16)},${parseInt(m6[2], 16)},${parseInt(m6[3], 16)},1)`;
    }
    return hex;
  }
  